import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { T_KEYS } from '../../constants';

interface GathernPhoneStepProps {
  onSubmit: (phoneNumber: string) => void;
  isLoading: boolean;
  error?: string;
}

const GathernPhoneStep: FC<GathernPhoneStepProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>('');

  // Validate phone number using the library's validation
  const isValidPhone = phoneNumber ? isValidPhoneNumber(phoneNumber) : false;

  const handlePhoneChange = useCallback((value: string | undefined) => {
    setPhoneNumber(value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (isValidPhone && !isLoading && phoneNumber) {
      onSubmit(phoneNumber);
    }
  }, [isValidPhone, isLoading, phoneNumber, onSubmit]);

  return (
    <Container>
      <Header>
        <Title>{t(T_KEYS.GATHERN_LOGIN_TITLE)}</Title>
        <Description>
          {t(T_KEYS.GATHERN_LOGIN_DESC)}
        </Description>
      </Header>

      <InputSection>
        <InputLabel>{t(T_KEYS.PHONE_NUMBER)}</InputLabel>
        <PhoneInputWrapper $hasError={!!error} $isRtl={isRtl}>
          <PhoneInput
            international
            defaultCountry="SA"
            placeholder={t(T_KEYS.ENTER_PHONE_NUMBER)}
            value={phoneNumber}
            onChange={handlePhoneChange}
            disabled={isLoading}
            countryCallingCodeEditable={false}
          />
        </PhoneInputWrapper>
        {error && <ErrorText>{error}</ErrorText>}
      </InputSection>

      <SubmitButton 
        onClick={handleSubmit}
        disabled={!isValidPhone || isLoading}
        $disabled={!isValidPhone || isLoading}
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          t(T_KEYS.CONTINUE)
        )}
      </SubmitButton>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #262626;
  margin: 0;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #262626;
`;

const PhoneInputWrapper = styled.div<{ $hasError?: boolean; $isRtl?: boolean }>`
  background: #f8f8f8;
  border: 1px solid ${({ $hasError }) => $hasError ? '#d32f2f' : 'transparent'};
  border-radius: 16px;
  padding: 4px 16px;
  
  &:focus-within {
    border-color: ${({ $hasError }) => $hasError ? '#d32f2f' : '#5b2ff9'};
  }

  /* Style the phone input component */
  .PhoneInput {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: ${({ $isRtl }) => $isRtl ? 'row-reverse' : 'row'};
  }

  .PhoneInputCountry {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }

  .PhoneInputCountryIcon {
    width: 24px;
    height: 18px;
    border-radius: 2px;
    overflow: hidden;
  }

  .PhoneInputCountryIcon--border {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }

  .PhoneInputCountrySelectArrow {
    width: 8px;
    height: 8px;
    border-style: solid;
    border-color: #666;
    border-width: 0 1px 1px 0;
    transform: rotate(45deg);
    margin-left: 4px;
  }

  .PhoneInputInput {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 16px;
    line-height: 24px;
    color: #262626;
    outline: none;
    height: 40px;
    /* Phone numbers should always be LTR and start from left */
    direction: ltr;
    text-align: left;

    &::placeholder {
      color: #666666;
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  /* Country select dropdown */
  .PhoneInputCountrySelect {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    border: 0;
    opacity: 0;
    cursor: pointer;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #d32f2f;
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  height: 56px;
  background: ${({ $disabled }) => $disabled ? '#dddddd' : '#5b2ff9'};
  color: ${({ $disabled }) => $disabled ? '#bdbdbd' : 'white'};
  border: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 56px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ $disabled }) => $disabled ? '#dddddd' : '#4a25d6'};
  }
`;

export default GathernPhoneStep;
